/**
 * cardioUnits.ts — sport-aware rate-unit resolver (frontend twin).
 *
 * MIRROR OF `services/cardio_units.py`. The two MUST stay in lockstep — the
 * tile an athlete reads and the prompt Cedar reasons over have to use the
 * same vocabulary, or Cedar narrates one unit while the screen shows another.
 * `tests/gate/test_cardio_units.py` pins the label tables across both files.
 *
 * Origin: p.rush12 (`810c13d3`) 2026-07-23 road ride — 13.95 km in 33:03
 * (25.3 km/h) rendered as "Avg Pace 2:22/km" on the completed-workout card,
 * directly above a GPS map that correctly said 25.3 km/h. Cedar had exactly
 * one rate vocabulary and applied it to every distance sport.
 *
 * THE STANDARD
 *   Running / walking / hiking            → min/km
 *   Cycling (road, gravel, MTB, indoor)   → km/h
 *   Swimming (pool + open water)          → min/100m
 *   Rowing / erg / kayak / SUP            → min/500m  ("split")
 *   Skating / skiing                      → km/h
 *   Elliptical / stair climber            → no rate
 *   Ball + racquet sports                 → no rate
 *
 * The last two matter as much as the rest. An elliptical's distance is a
 * machine estimate from stride count and a tennis match's GPS trace is
 * wandering, not travelling — a rate derived from either is noise wearing a
 * number's clothes. Cedar declines to show it rather than invent precision.
 *
 * UNKNOWN LABELS DEGRADE TO PACE, NOT TO SILENCE. An unmapped label is far
 * more likely to be a running variant (Cedar's dominant modality) than a
 * cycling one, and dropping the rate tile on a real run is a worse
 * regression than continuing today's behavior on an exotic label.
 */

export type RateUnit =
  | 'pace_per_km'
  | 'speed_kmh'
  | 'pace_per_100m'
  | 'pace_per_500m'
  // UNITS-U4 (owner ruling #42): DISPLAY units. resolveRateUnit never returns
  // these; resolveDisplayRateUnit maps the sport's convention onto the athlete's
  // miles preference, the pool (yards) and the sailing family (knots, M5).
  | 'pace_per_mi'
  | 'speed_mph'
  | 'speed_knots'
  | 'pace_per_100yd'
  | 'none';

// ─────────────────────────────────────────────────────────────────────
// Label tables — keep byte-for-byte in sync with services/cardio_units.py
// ─────────────────────────────────────────────────────────────────────

const PACE_PER_KM_LABELS = new Set<string>([
  'running', 'run', 'jogging', 'jog',
  'treadmill', 'treadmill_running', 'indoor_running', 'virtual_run',
  'trail_running', 'trail_run', 'track_running', 'street_running',
  'obstacle_run', 'ultra_run',
  'walking', 'walk', 'fitness_walking', 'casual_walking', 'speed_walking',
  'hiking', 'hike', 'rucking', 'snowshoeing', 'snowshoe',
  'stroller_walking', 'wheelchair_walk_pace', 'wheelchair_run_pace',
]);

const SPEED_KMH_LABELS = new Set<string>([
  'cycling', 'cycle', 'bike', 'biking', 'ride', 'bicycling',
  'road_cycling', 'road_biking', 'outdoor_cycling',
  'mountain_biking', 'mountain_bike', 'gravel_cycling', 'gravel_biking',
  'cyclocross', 'track_cycling', 'bmx',
  'indoor_cycling', 'virtual_ride', 'spinning', 'handcycling',
  'e_bike', 'ebike', 'e_bike_fitness', 'electric_bike',
  'commuting', 'bike_commuting', 'recumbent_bike',
  'skating', 'skate', 'ice_skating', 'inline_skating', 'rollerblading',
  'skateboarding', 'roller_skiing',
  'cross_country_skiing', 'skiing', 'downhill_skiing', 'alpine_skiing',
  'backcountry_skiing', 'snowboarding', 'sledding',
  'kitesurfing', 'windsurfing', 'sailing',
]);

const PACE_PER_100M_LABELS = new Set<string>([
  'swimming', 'swim', 'lap_swimming', 'pool_swim', 'pool_swimming',
  'open_water_swimming', 'open_water_swim', 'openwater_swimming',
]);

const PACE_PER_500M_LABELS = new Set<string>([
  'rowing', 'row', 'indoor_rowing', 'rowing_machine', 'erg', 'ergometer',
  'kayaking', 'kayak', 'canoeing', 'canoe', 'whitewater_rafting_kayaking',
  'stand_up_paddleboarding', 'paddleboarding', 'paddling', 'paddle_sports',
  'stand_up_paddling', 'outrigger_canoeing',
  // Endurance vertical PR-6a: the SkiErg monitor reports the rowing erg /500 m split.
  'skierg', 'ski_erg',
]);

const NO_RATE_LABELS = new Set<string>([
  'elliptical', 'cross_trainer', 'stair_climbing', 'stairclimber',
  'stair_climber', 'stepper', 'step_machine', 'climbing_machine',
  'arc_trainer', 'jump_rope', 'skipping',
  'soccer', 'football', 'american_football', 'basketball', 'baseball',
  'softball', 'volleyball', 'hockey', 'ice_hockey', 'field_hockey',
  'lacrosse', 'cricket', 'rugby', 'handball', 'ultimate_disc',
  'tennis', 'table_tennis', 'badminton', 'squash', 'racquetball',
  'pickleball', 'padel', 'golf', 'disc_golf',
  'boxing', 'kickboxing', 'martial_arts', 'mma', 'wrestling',
  'climbing', 'rock_climbing', 'bouldering', 'indoor_climbing',
  // NOTE: no apostrophes inside this Set block. tests/gate/test_cardio_units.py
  // extracts these labels with a single-quote regex, so a contraction in a
  // comment silently truncates the parsed set and fails the lockstep check.
  //
  // The Cedar activity_type bucket for sport/skill sessions (the value
  // derive_activity_type emits for climbing, hockey, tennis, martial arts).
  // It is never a running variant, so the "unknown to pace" default is wrong
  // for it. The two rate-bearing members of the bucket — skiing-snowboarding
  // and paddling — always classify earlier from their own workout_type, so
  // they never reach this row.
  'sports',
  'dance', 'dancing', 'gymnastics', 'horseback_riding', 'fishing',
  'hunting', 'surfing', 'diving', 'snorkeling', 'water_polo',
  'strength', 'strength_training', 'weight_training', 'weightlifting',
  'functional_strength_training', 'traditional_strength_training',
  'powerlifting', 'olympic_lifting', 'bodybuilding', 'core_training',
  'yoga', 'pilates', 'mobility', 'stretching', 'flexibility',
  'meditation', 'breathwork', 'mind_and_body', 'foam_rolling',
  'hiit', 'tabata', 'circuit_training', 'crossfit', 'boot_camp',
]);

/** Order matters — longest / most specific first. */
const PREFIX_TABLE: Array<[string, RateUnit]> = [
  ['strength-', 'none'],
  ['strength_', 'none'],
  ['mobility-', 'none'],
  ['yoga-', 'none'],
  ['pilates-', 'none'],
  ['stretching-', 'none'],
  ['recovery-', 'none'],
  ['hiit-', 'none'],
  ['crossfit-', 'none'],
  // 🧗 The six first-class climbing types (taxonomy sprint 2026-06-22):
  // climbing-hangboard / -limit-boulder / -projecting / -power-endurance /
  // -technique / -antagonist. Phase-based sport work — vertical metres are not
  // a travel rate, and the horizontal GPS trace of a wall session is noise. The
  // bare rock-climbing and climbing labels are already exact no-rate rows; this
  // covers the compound family.
  ['climbing-', 'none'],
  ['cycling-', 'speed_kmh'],
  ['cycling_', 'speed_kmh'],
  ['bike-', 'speed_kmh'],
  ['biking-', 'speed_kmh'],
  ['skiing-', 'speed_kmh'],
  ['skating-', 'speed_kmh'],
  ['swimming-', 'pace_per_100m'],
  ['swimming_', 'pace_per_100m'],
  ['swim-', 'pace_per_100m'],
  ['rowing-', 'pace_per_500m'],
  ['rowing_', 'pace_per_500m'],
  ['paddling-', 'pace_per_500m'],
  ['kayaking-', 'pace_per_500m'],
  ['running-', 'pace_per_km'],
  ['running_', 'pace_per_km'],
  ['walking-', 'pace_per_km'],
  ['walking_', 'pace_per_km'],
  ['hiking-', 'pace_per_km'],
  ['hiking_', 'pace_per_km'],
  ['fitness-test-cycling', 'speed_kmh'],
  ['fitness-test-swimming', 'pace_per_100m'],
  ['fitness-test-rowing', 'pace_per_500m'],
  // Endurance PR-8b2c-3: without this row the 'ski' substring would read a
  // SkiErg test as a speed in km/h. A SkiErg split is per 500 m.
  ['fitness-test-skierg', 'pace_per_500m'],
  ['fitness-test-running', 'pace_per_km'],
];

/**
 * Substring patterns — the LAST resort before the pace default.
 * MIRROR of _SUBSTRING_PATTERNS in services/cardio_units.py.
 *
 * Enumerating labels isn't enough: the ingesters don't share a vocabulary and
 * several pass vendor strings through verbatim. Strava writes its raw enum name
 * ('InlineSkate', 'EBikeRide', 'GravelRide', 'MountainBikeRide', 'AlpineSki',
 * 'IceSkate', 'Snowboard', 'Kitesurf', 'StandUpPaddling', 'Handcycle',
 * 'Velomobile', 'StairStepper'); Garmin sends SCREAMING_SNAKE variants
 * ('DOWNHILL_BIKING', 'ROAD_BIKING', 'CYCLOCROSS'). All of them fell through to
 * the pace default and reproduced the origin bug.
 *
 * Runs AFTER the exact-match sets and the prefix table so a specific row always
 * wins — 'ski_erg' is an exact no-rate entry and must not be caught by 'ski'.
 */
const SUBSTRING_PATTERNS: Array<[string, RateUnit]> = [
  ['bik', 'speed_kmh'],
  ['cycl', 'speed_kmh'],
  ['_ride', 'speed_kmh'],
  ['velomobile', 'speed_kmh'],
  ['skat', 'speed_kmh'],
  ['ski', 'speed_kmh'],
  ['snowboard', 'speed_kmh'],
  ['surf', 'speed_kmh'],
  ['swim', 'pace_per_100m'],
  ['paddl', 'pace_per_500m'],
  ['kayak', 'pace_per_500m'],
  ['canoe', 'pace_per_500m'],
  ['row', 'pace_per_500m'],
  ['elliptic', 'none'],
  ['stair', 'none'],
  ['stepper', 'none'],
  ['treadmill', 'pace_per_km'],
  ['run', 'pace_per_km'],
  ['jog', 'pace_per_km'],
  ['walk', 'pace_per_km'],
  ['hik', 'pace_per_km'],
];

/** Cedar's own generic buckets — defer to the next label rather than guess. */
const GENERIC_LABELS = new Set<string>([
  'cardio', 'other', 'unknown', 'workout', 'generic',
]);

const RATE_LABELS: Record<Exclude<RateUnit, 'none'>, string> = {
  pace_per_km: 'Avg Pace',
  speed_kmh: 'Avg Speed',
  pace_per_100m: 'Avg Pace',
  // Rowing's own word for its rate is "split", not "pace".
  pace_per_500m: 'Avg Split',
  pace_per_mi: 'Avg Pace',
  speed_mph: 'Avg Speed',
  speed_knots: 'Avg Speed',
  pace_per_100yd: 'Avg Pace',
};

const RATE_SUFFIXES: Record<Exclude<RateUnit, 'none'>, string> = {
  pace_per_km: '/km',
  speed_kmh: ' km/h',
  pace_per_100m: '/100m',
  pace_per_500m: '/500m',
  pace_per_mi: '/mi',
  speed_mph: ' mph',
  speed_knots: ' kn',
  pace_per_100yd: '/100yd',
};

/** Short unit strings for split/segment tables and chart axes. */
const RATE_AXIS_UNITS: Record<Exclude<RateUnit, 'none'>, string> = {
  pace_per_km: '/km',
  speed_kmh: 'km/h',
  pace_per_100m: '/100m',
  pace_per_500m: '/500m',
  pace_per_mi: '/mi',
  speed_mph: 'mph',
  speed_knots: 'kn',
  pace_per_100yd: '/100yd',
};

// UNITS-U4: metres in one display unit (paces) and speed per m/s (speeds).
// Mirror of services/cardio_units.py _PACE_METRES / _SPEED_PER_MPS.
export const METERS_PER_MILE = 1609.344;
export const METERS_PER_YARD = 0.9144;
const METERS_PER_NAUTICAL_MILE = 1852;
const PACE_METRES: Partial<Record<RateUnit, number>> = {
  pace_per_km: 1000,
  pace_per_100m: 100,
  pace_per_500m: 500,
  pace_per_mi: METERS_PER_MILE,
  pace_per_100yd: 100 * METERS_PER_YARD,
};
const SPEED_PER_MPS: Partial<Record<RateUnit, number>> = {
  speed_kmh: 3.6,
  speed_mph: 3600 / METERS_PER_MILE,
  speed_knots: 3600 / METERS_PER_NAUTICAL_MILE,
};

// A rate outside these bounds is a data artifact (GPS dropout, tracker left
// running, machine-fabricated distance) — not an athlete. Show nothing.
const MIN_PLAUSIBLE_MPS = 0.15; // ~0.5 km/h
const MAX_PLAUSIBLE_MPS = 36.0; // 130 km/h

const unknownLogged = new Set<string>();
const UNKNOWN_LOG_CAP = 256;

function logUnknownLabel(label: string): void {
  if (unknownLogged.size >= UNKNOWN_LOG_CAP || unknownLogged.has(label)) return;
  unknownLogged.add(label);
  // eslint-disable-next-line no-console
  console.log(
    `CARDIO_UNIT_UNKNOWN_LABEL label="${label}" — defaulting to pace/km. ` +
      'Consider adding to cardioUnits.ts + services/cardio_units.py.',
  );
}

/**
 * Lowercase, camelCase→snake_case, strip Garmin `_v2` suffixes.
 * Garmin sends `tennis_v2`; Apple HealthKit sends `lapSwimming`.
 */
function normalize(label?: string | null): string {
  if (!label) return '';
  let text = String(label).trim();
  if (!text) return '';
  let out = '';
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const prev = i > 0 ? text[i - 1] : '';
    if (ch >= 'A' && ch <= 'Z' && prev && prev >= 'a' && prev <= 'z') out += '_';
    out += ch.toLowerCase();
  }
  // 🧗 2026-07-28 — HYPHENS were the one separator this never unified, and the
  // Cedar taxonomy is hyphen-authored (rock-climbing, martial-arts,
  // climbing-hangboard). So every hyphenated type the prefix table did not
  // happen to cover missed the underscore-keyed exact sets and fell through to
  // the pace/km default: a rock-climbing session rendered "Avg Pace 2:22/km".
  // Origin: mattmayer (`e9378d10`) timer log,
  // `CARDIO_UNIT_UNKNOWN_LABEL label="rock-climbing"`.
  //
  // The prefix table is authored with hyphens ('cycling-', 'fitness-test-
  // running'), so it is normalized through THIS function too — see
  // PREFIX_TABLE_NORMALIZED. Unifying here without that would have broken every
  // hyphen-only prefix row ('mobility-', 'yoga-', 'hiit-', 'bike-', 'skiing-',
  // the four 'fitness-test-*'), which have no underscore twin.
  text = out.replace(/[\s/-]/g, '_');
  if (text.endsWith('_v2') || text.endsWith('_v3')) text = text.slice(0, -3);
  return text;
}

/**
 * The prefix table is authored with hyphens for readability, but `normalize`
 * unifies separators — so the keys must go through the same funnel or a
 * hyphen-only row could never match. Computed once, not per call.
 * (`normalize` is a function declaration, so it is hoisted above this const.)
 */
const PREFIX_TABLE_NORMALIZED: Array<[string, RateUnit]> = PREFIX_TABLE.map(
  ([prefix, unit]) => [normalize(prefix), unit] as [string, RateUnit],
);

/** Classify one label. `null` means "not confident" — try the next. */
function classifyOne(label?: string | null): RateUnit | null {
  const n = normalize(label);
  if (!n) return null;

  if (PACE_PER_KM_LABELS.has(n)) return 'pace_per_km';
  if (SPEED_KMH_LABELS.has(n)) return 'speed_kmh';
  if (PACE_PER_100M_LABELS.has(n)) return 'pace_per_100m';
  if (PACE_PER_500M_LABELS.has(n)) return 'pace_per_500m';
  if (NO_RATE_LABELS.has(n)) return 'none';

  for (const [prefix, unit] of PREFIX_TABLE_NORMALIZED) {
    if (n.startsWith(prefix)) return unit;
  }

  // Generic buckets BEFORE the substring patterns, or 'workout' would be
  // caught by the 'row'/'run' fragments. No unknown-label log — expected.
  if (GENERIC_LABELS.has(n)) return null;

  for (const [fragment, unit] of SUBSTRING_PATTERNS) {
    if (n.includes(fragment)) return unit;
  }

  logUnknownLabel(n);
  return null;
}

/**
 * Resolve which rate vocabulary this activity should be described in.
 *
 * Pass every label available, MOST SPECIFIC FIRST — typically
 * `resolveRateUnit(workout_type, activity_type, sessionType)`. The first
 * confidently-classified label wins, which matters for Cedar in-app
 * sessions: they carry a generic `activity_type='cardio'` alongside a
 * specific `workout_type='cycling-road'`.
 *
 * Defaults to `'pace_per_km'` when nothing classifies (pre-sprint behavior).
 */
export function resolveRateUnit(
  ...labels: Array<string | null | undefined>
): RateUnit {
  for (const label of labels) {
    const unit = classifyOne(label);
    if (unit !== null) return unit;
  }
  return 'pace_per_km';
}

function asFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || typeof value === 'boolean') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Funnel every rate representation Cedar stores into metres/second.
 *
 * Precedence — MEASURED before DERIVED (mirror of services/cardio_units.py):
 *   1. speedMps           — the device's own average speed
 *   2. pacePerKmSeconds   — the device's own average rate
 *   3. distance / duration — derived fallback
 *
 * (1) and (2) are the same class of fact: a number the watch reported. (3) is
 * arithmetic we do ourselves, and `durationSeconds` is usually ELAPSED time
 * while a device average is often MOVING time — so on any activity with a
 * stop it reads slower than the truth. Ranking derived above measured also
 * drifted the rendered value by a second from what the rest of the app shows.
 *
 * Returns null when no input yields a plausible speed.
 */
export function speedMpsFrom(input: {
  speedMps?: number | null;
  distanceMeters?: number | null;
  durationSeconds?: number | null;
  pacePerKmSeconds?: number | null;
}): number | null {
  const candidates: Array<number | null> = [];

  candidates.push(asFiniteNumber(input.speedMps));

  const pace = asFiniteNumber(input.pacePerKmSeconds);
  if (pace !== null && pace > 0) candidates.push(1000 / pace);

  const dist = asFiniteNumber(input.distanceMeters);
  const dur = asFiniteNumber(input.durationSeconds);
  if (dist !== null && dur !== null && dist > 0 && dur > 0) candidates.push(dist / dur);

  for (const v of candidates) {
    if (v === null) continue;
    if (v >= MIN_PLAUSIBLE_MPS && v <= MAX_PLAUSIBLE_MPS) return v;
  }
  return null;
}

/**
 * Render the rate in `unit`'s vocabulary, or null if not renderable.
 *
 * Returns null — never "0:00", never "--:--" — when the sport has no rate
 * concept or the inputs can't support an honest number. Callers omit the
 * tile entirely. Fabricating a placeholder is what produced the
 * `0:00 - 0:00` cycling pace targets already sitting in production.
 */
export function formatRate(
  unit: RateUnit,
  input: {
    speedMps?: number | null;
    distanceMeters?: number | null;
    durationSeconds?: number | null;
    pacePerKmSeconds?: number | null;
    includeSuffix?: boolean;
  },
): string | null {
  if (unit === 'none') return null;

  const mps = speedMpsFrom(input);
  if (mps === null) return null;

  const suffix = input.includeSuffix === false ? '' : RATE_SUFFIXES[unit];

  const perMps = SPEED_PER_MPS[unit];
  if (perMps !== undefined) return `${(mps * perMps).toFixed(1)}${suffix}`;

  const metres = PACE_METRES[unit] ?? 100;
  const total = Math.round(metres / mps);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}${suffix}`;
}

/** Display label for the rate tile ("Avg Speed", "Avg Split", …). */
export function rateLabel(unit: RateUnit): string | null {
  return unit === 'none' ? null : RATE_LABELS[unit];
}

/** Short unit for split tables and chart axes ("km/h", "/100m", …). */
export function rateAxisUnit(unit: RateUnit): string | null {
  return unit === 'none' ? null : RATE_AXIS_UNITS[unit];
}

/**
 * True when this unit reads "bigger number = faster" (speed) rather than
 * "smaller number = faster" (pace). Chart axes and delta colouring invert.
 */
export function isSpeedUnit(unit: RateUnit): boolean {
  return SPEED_PER_MPS[unit] !== undefined;
}

/**
 * Convert a stored seconds-per-km value into the display unit's numeric
 * value — for charts and tables that need the number, not the string.
 * Returns null when the unit has no rate or the input is implausible.
 */
export function rateValueFromPacePerKm(
  unit: RateUnit,
  pacePerKmSeconds?: number | null,
): number | null {
  if (unit === 'none') return null;
  const mps = speedMpsFrom({ pacePerKmSeconds });
  if (mps === null) return null;
  const perMps = SPEED_PER_MPS[unit];
  if (perMps !== undefined) return mps * perMps;
  const metres = PACE_METRES[unit] ?? 100;
  return metres / mps;
}

/**
 * Format an already-resolved numeric rate value (the output of
 * `rateValueFromPacePerKm`) — for axis ticks where the caller holds the
 * number. Speed formats as one decimal; paces format as m:ss.
 */
export function formatRateValue(
  unit: RateUnit,
  value: number,
  includeSuffix = true,
): string {
  const suffix = includeSuffix ? RATE_SUFFIXES[unit as Exclude<RateUnit, 'none'>] ?? '' : '';
  if (isSpeedUnit(unit)) return `${value.toFixed(1)}${suffix}`;
  const total = Math.round(value);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}${suffix}`;
}

// ═══════════════════════════════════════════════════════════════════════
// UNITS-U4 (owner ruling #42) — the athlete's DISPLAY unit.
// Mirror of services/cardio_units.py resolve_display_rate_unit and the
// format_* helpers; tests/fixtures/units_golden_vectors.json binds both.
//   * run/walk/hike /km -> /mi and bike/ski/skate km/h -> mph ONLY for a
//     miles athlete;
//   * the sailing family -> knots for everyone (M5);
//   * a YARD pool swim -> /100yd for everyone (M2: the pool decides);
//   * rowing / erg / paddle /500m untouched.
// `enabled=false` (the server did not opt this athlete in) returns the sport
// unit unchanged — today's output exactly.
// ═══════════════════════════════════════════════════════════════════════

const SAILING_FAMILY = new Set<string>([
  'sailing', 'sail', 'kitesurfing', 'kitesurf', 'kiteboarding',
  'windsurfing', 'windsurf', 'wingfoiling', 'wing_foiling',
  // FIX-UNITS-1 F-sail: separator dialects (mirror of services/cardio_units.py).
  'kite_surfing', 'kite_boarding', 'kiteboard', 'wind_surfing', 'wing_foil', 'wingfoil',
]);
const YARD_POOL_LENGTHS = [20, 25, 100 / 3, 50];
const YARD_TOLERANCE_M = 0.15;

// FIX-UNITS-1 item 7 (R2-§OW): an OPEN-WATER swim reads in metres for everyone.
// Mirror of _OPEN_WATER_SWIM_LABELS in services/cardio_units.py.
const OPEN_WATER_SWIM_LABELS = new Set<string>([
  'open_water_swimming', 'open_water_swim', 'openwater_swimming', 'openwater_swim',
  'open_water',
]);

export function isOpenWaterSwim(...labels: Array<string | null | undefined>): boolean {
  return labels.some((l) => OPEN_WATER_SWIM_LABELS.has(normalize(l)));
}

export function isSailingFamily(...labels: Array<string | null | undefined>): boolean {
  return labels.some((l) => SAILING_FAMILY.has(normalize(l)));
}

export function isYardPool(poolLengthM: unknown): boolean {
  const m = asFiniteNumber(poolLengthM);
  if (m === null || m < 15 || m > 60) return false;
  return YARD_POOL_LENGTHS.some((yd) => Math.abs(m - yd * METERS_PER_YARD) <= YARD_TOLERANCE_M);
}

export function resolveDisplayRateUnit(
  sportUnit: RateUnit,
  opts: {
    distanceUnit?: string | null;
    poolLengthM?: unknown;
    labels?: Array<string | null | undefined>;
    enabled?: boolean;
  } = {},
): RateUnit {
  if (opts.enabled === false || sportUnit === 'none') return sportUnit;
  // F-sail: a sailing label that fell to the pace default ('Sail', 'kiteboarding',
  // 'wingfoiling') reads knots too; the sport classification is unchanged.
  if ((sportUnit === 'speed_kmh' || sportUnit === 'pace_per_km')
    && isSailingFamily(...(opts.labels ?? []))) return 'speed_knots';
  const miles = String(opts.distanceUnit ?? '').trim().toLowerCase() === 'miles';
  if (sportUnit === 'pace_per_km' && miles) return 'pace_per_mi';
  if (sportUnit === 'speed_kmh' && miles) return 'speed_mph';
  if (sportUnit === 'pace_per_100m' && isYardPool(opts.poolLengthM)) return 'pace_per_100yd';
  return sportUnit;
}

/** Multisport labels (FIX-UNITS-3 item 4). Twin of services/cardio_units.py MULTISPORT_WORDS. */
export const MULTISPORT_WORDS = [
  'triathlon', 'brick', 'duathlon', 'aquathlon', 'swimrun', 'multisport', 'multi_sport', 'multi-sport',
] as const;

/** Whether any label names a multisport recording. */
export function isMultisport(...labels: Array<string | null | undefined>): boolean {
  return labels.some((l) => MULTISPORT_WORDS.some((w) => String(l ?? '').toLowerCase().includes(w)));
}

/**
 * FIX-UNITS-2 item 4 (P2-2): whether a miles athlete's splits for THIS sport run
 * by the mile. The U4 rate rule decides: only a sport that reads /mi or mph for a
 * miles athlete (run, walk, hike — and rides, which follow the rate rule to mph)
 * splits by the mile; a swim (open water included), a row or paddle and the
 * sailing family never do. Twin of services/cardio_units.py mile_splits_apply
 * (shared vectors: tests/fixtures/units_golden_vectors.json "mile_splits").
 */
export function mileSplitsApply(...labels: Array<string | null | undefined>): boolean {
  if (isOpenWaterSwim(...labels)) return false;
  // FIX-UNITS-3 item 4: a multisport recording holds a swim too — never by the mile.
  if (isMultisport(...labels)) return false;
  const shown = resolveDisplayRateUnit(resolveRateUnit(...labels), { distanceUnit: 'miles', labels });
  return shown === 'pace_per_mi' || shown === 'speed_mph';
}

const isMiles = (u?: string | null) => String(u ?? '').trim().toLowerCase() === 'miles';

/** "5.00 km" / "3.11 mi", or null. */
export function formatDistance(meters: unknown, distanceUnit?: string | null, decimals = 2): string | null {
  const m = asFiniteNumber(meters);
  if (m === null || m < 0) return null;
  return isMiles(distanceUnit)
    ? `${(m / METERS_PER_MILE).toFixed(decimals)} mi`
    : `${(m / 1000).toFixed(decimals)} km`;
}

/**
 * A distance that is a NAME, not a measurement — a rulebook course leg (HYROX
 * "Run 1 km"), like a race name ("10K"): kilometres for every athlete, never
 * converted. Twin of services/cardio_units.distance_name_km (UNITS-PR-FINAL, U7).
 */
export function distanceNameKm(numberText: unknown): string {
  return `${String(numberText).trim()} km`;
}

/**
 * A pace prescribed against a km-defined course ("4:30/km" on a HYROX run leg):
 * the course's own unit, never converted (UNITS-PR-FINAL, U7).
 */
export function coursePacePerKmText(pace: string): string {
  return `${pace}${RATE_SUFFIXES.pace_per_km}`;
}

/** Elevation follows the DISTANCE preference (miles pairs with feet). */
export function formatElevation(meters: unknown, distanceUnit?: string | null): string | null {
  const m = asFiniteNumber(meters);
  if (m === null) return null;
  return isMiles(distanceUnit) ? `${Math.round(m / 0.3048)} ft` : `${Math.round(m)} m`;
}

export function formatTemperature(celsius: unknown, temperatureUnit?: string | null): string | null {
  const c = asFiniteNumber(celsius);
  if (c === null) return null;
  return String(temperatureUnit ?? '').trim().toLowerCase() === 'fahrenheit'
    ? `${Math.round(c * 9 / 5 + 32)}°F`
    : `${Math.round(c)}°C`;
}

/** Wind follows the DISTANCE preference, never temperature (a Briton: °C + mph). */
export function formatWind(kmh: unknown, distanceUnit?: string | null): string | null {
  const v = asFiniteNumber(kmh);
  if (v === null || v < 0) return null;
  return isMiles(distanceUnit) ? `${Math.round(v * 1000 / METERS_PER_MILE)} mph` : `${Math.round(v)} km/h`;
}
